import type { MouseEvent } from 'react';
import { isIsoDate, weekOf } from '../core/calendar';
import type { YearMonth } from '../core/month';
import type { IsoDate } from '../core/types';

/**
 * Hand-rolled routes on the History API (KTD10): home, the plan list, a new plan, the editor,
 * the weekly view, My week, and a topic read in place (R12). Home opens My week or the plan
 * opened last (R4, R13). My week carries the planbook's level in its path (R7, KTD4).
 */
export type Zoom = 'year' | 'month' | 'week';
/** Where the planbook is: the year, a month, or a week by its Monday. */
export type PlanbookAt = { zoom: 'year' } | { zoom: 'month'; month: YearMonth } | { zoom: 'week'; monday: IsoDate };

export type Route =
  | { name: 'home' }
  | { name: 'plans' }
  | { name: 'new' }
  | { name: 'plan'; id: string }
  | { name: 'week'; id: string }
  | { name: 'my_week'; at: PlanbookAt }
  | { name: 'topic'; id: string };

const decode = (value: string) => { try { return decodeURIComponent(value); } catch { return null; } };

export function parseRoute(pathname: string): Route {
  if (/^\/plans\/?$/.test(pathname)) return { name: 'plans' };
  if (/^\/plans\/new\/?$/.test(pathname)) return { name: 'new' };
  const myWeek = /^\/week(?:\/([^/]*))?\/?$/.exec(pathname);
  if (myWeek) return { name: 'my_week', at: planbookAt(myWeek[1] ?? '') };
  if (/^\/week\//.test(pathname)) return { name: 'my_week', at: { zoom: 'year' } };
  const match = /^\/(plans|topics)\/([^/]+)(\/week)?\/?$/.exec(pathname);
  const id = match ? decode(match[2]!) : null;
  if (!match || id === null) return { name: 'home' };
  if (match[1] === 'topics') return match[3] ? { name: 'home' } : { name: 'topic', id };
  return { name: match[3] ? 'week' : 'plan', id };
}

/** KTD4: `YYYY-MM` is a month, `YYYY-MM-DD` the week it falls in; anything else is the year. */
function planbookAt(segment: string): PlanbookAt {
  const month = /^(\d{4})-(\d{2})$/.exec(segment);
  if (month && Number(month[2]) >= 1 && Number(month[2]) <= 12) return { zoom: 'month', month: { year: Number(month[1]), month: Number(month[2]) } };
  if (isIsoDate(segment)) return { zoom: 'week', monday: weekOf(segment) };
  return { zoom: 'year' };
}

export const PLANS_HREF = '/plans';
export const NEW_PLAN_HREF = '/plans/new';
export const MY_WEEK_HREF = '/week';
export const planHref = (id: string) => `/plans/${encodeURIComponent(id)}`;
export const weekHref = (id: string) => `${planHref(id)}/week`;
export const planbookHref = (at: PlanbookAt) => {
  if (at.zoom === 'month') return `${MY_WEEK_HREF}/${at.month.year}-${String(at.month.month).padStart(2, '0')}`;
  return at.zoom === 'week' ? `${MY_WEEK_HREF}/${at.monday}` : MY_WEEK_HREF;
};
export const topicHref = (id: string) => `/topics/${encodeURIComponent(id)}`;
/** The planbook at the week of `today`. */
export const thisWeekHref = (today: IsoDate) => planbookHref({ zoom: 'week', monday: weekOf(today) });
/**
 * A plan's week: with the planbook on, the planbook at the week of `planbookToday`, which retires
 * the plan's own weekly view; with it off (null), that weekly view.
 */
export const planWeekHref = (id: string, planbookToday: IsoDate | null = null) =>
  (planbookToday ? thisWeekHref(planbookToday) : weekHref(id));
/** A built plan opens on its week (`planWeekHref`); one still being built opens in the editor (R13). */
export const landingHref = (plan: { id: string; built: boolean }, planbookToday: IsoDate | null = null) =>
  (plan.built ? planWeekHref(plan.id, planbookToday) : planHref(plan.id));
/** The canonical path for a route; anything unrecognised is home. */
export function routeHref(route: Route): string {
  switch (route.name) {
    case 'home': return '/';
    case 'plans': return PLANS_HREF;
    case 'new': return NEW_PLAN_HREF;
    case 'plan': return planHref(route.id);
    case 'week': return weekHref(route.id);
    case 'my_week': return planbookHref(route.at);
    case 'topic': return topicHref(route.id);
  }
}

/** Moves to a route in place; App listens for `popstate`, so Back and Forward work the same way. */
export function navigate(href: string, replace = false) {
  if (replace) window.history.replaceState(null, '', href);
  else window.history.pushState(null, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** An in-app link that still opens in a new tab on a modified click. */
export const linkTo = (href: string, before?: () => void) => ({
  href,
  onClick(event: MouseEvent<HTMLAnchorElement>) {
    if (!isPlainClick(event)) return;
    event.preventDefault(); before?.(); navigate(href);
  },
});

export function isPlainClick(event: MouseEvent<HTMLAnchorElement>) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !event.defaultPrevented;
}
